import { Eye, EyeOff, GitBranchPlus, Pencil } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { SkillDetail } from "@/hooks/use-skill";
import { useTogglePublish } from "@/hooks/use-skill-mutations";
import { EditMetadataDialog } from "./EditMetadataDialog";
import { SubmitVersionDialog } from "./SubmitVersionDialog";

export function OwnerTools({
  skill,
  isAdmin,
  isOwner,
}: {
  skill: SkillDetail;
  isAdmin: boolean;
  isOwner: boolean;
}) {
  const toggle = useTogglePublish(skill.slug);
  const [editOpen, setEditOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  if (!isAdmin && !isOwner) return null;
  // Owners and admins can both edit metadata + submit versions; publish is admin-only.
  const canManage = isOwner || isAdmin;
  return (
    <div className="rounded-[12px] border border-border-1 bg-white p-5">
      <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-3">
        {isAdmin ? "Moderation" : "Owner tools"}
      </h4>
      <div className="flex flex-col gap-2.5">
        {canManage ? (
          <>
            <Button
              variant="outline"
              className="w-full justify-center"
              onClick={() => setVersionOpen(true)}
            >
              <GitBranchPlus className="size-4" />
              Submit new version
            </Button>
            <Button
              variant="outline"
              className="w-full justify-center"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-4" />
              Edit metadata
            </Button>
          </>
        ) : null}
        {isAdmin ? (
          <Button
            variant={skill.published ? "destructive" : "default"}
            className="w-full justify-center"
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(!skill.published)}
          >
            {skill.published ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            {skill.published ? "Unpublish skill" : "Publish skill"}
          </Button>
        ) : null}
      </div>
      {canManage ? (
        <>
          <EditMetadataDialog slug={skill.slug} open={editOpen} onOpenChange={setEditOpen} />
          <SubmitVersionDialog
            slug={skill.slug}
            currentVersion={skill.version}
            open={versionOpen}
            onOpenChange={setVersionOpen}
          />
        </>
      ) : null}
    </div>
  );
}
